import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type EvalDef={
  definitionId:string;
  code:string;
  name:string;
  scoreMin:number;
  scoreMax:number;
};

type VerifiedEvidence={
  evidenceId:string;
  learnerId:string;
  displayName:string;
  title:string;
  evidenceType:string;
  verifiedAt?:string|null;
};

type EvaluationRow={
  evaluationResultId:string;
  learnerId:string;
  displayName:string;
  definitionId:string;
  definitionName:string;
  status:string;
  version:number;
  numericScore?:number|null;
  normalizedScore?:number|null;
  narrative?:string|null;
  observedAt:string;
  confirmedAt?:string|null;
  evidence:Array<{
    evidenceId:string;
    title:string;
    status:string;
    type:string;
  }>;
  competencies:Array<{
    competencyResultId:string;
    competencyName:string;
    score:number;
    weight:number;
  }>;
};

type AchievementDef={
  definitionId:string;
  code:string;
  name:string;
  description?:string|null;
  criteria:Array<{
    criterionId:string;
    criterionKey:string;
    criterionType:string;
    required:boolean;
    minNormalizedScore?:number|null;
    minVerifiedEvidenceCount?:number|null;
    evaluationDefinitionId?:string|null;
    competencyId?:string|null;
  }>;
};

type AchievementRow={
  achievementId:string;
  learnerId:string;
  displayName:string;
  definitionId:string;
  definitionName:string;
  periodKey:string;
  status:string;
  version:number;
  criteriaSnapshot:unknown;
  confirmedAt?:string|null;
  linkedEvaluationCount:number;
  linkedEvidenceCount:number;
  credentialDefinitions:Array<{
    credentialDefinitionId:string;
    name:string;
    issuerOrganizationId:string;
    validityDays?:number|null;
    existingRequest?:{
      requestId:string;
      status:string;
      version:number;
      createdAt:string;
    }|null;
  }>;
};

type LearnerOption={
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
};

type Workbench={
  evaluationDefinitions:EvalDef[];
  verifiedEvidence:VerifiedEvidence[];
  evaluations:EvaluationRow[];
  achievementDefinitions:AchievementDef[];
  achievements:AchievementRow[];
  learners:LearnerOption[];
};

type Tab="EVALUATION"|"ACHIEVEMENT";

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function evalStatus(value:string){
  return value==="CONFIRMED" ? "확정" : "초안";
}

function achievementStatus(value:string){
  if(value==="CANDIDATE") return "후보";
  if(value==="CONFIRMED") return "확정";
  if(value==="VOIDED") return "무효";
  return value;
}

function criterionLabel(type:string){
  if(type==="EVALUATION_RESULT") return "평가결과";
  if(type==="COMPETENCY_SCORE") return "역량점수";
  if(type==="VERIFIED_EVIDENCE_COUNT") return "검증 증빙";
  return type;
}

export function CenterEvaluationAchievementWorkbench({
  client,
  schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const [data,setData]=useState<Workbench|null>(null);
  const [tab,setTab]=useState<Tab>("EVALUATION");
  const [selectedEvalId,setSelectedEvalId]=useState("");
  const [selectedAchievementId,setSelectedAchievementId]=useState("");
  const [selectedEvidenceId,setSelectedEvidenceId]=useState("");
  const [selectedEvalDefId,setSelectedEvalDefId]=useState("");
  const [score,setScore]=useState("80");
  const [narrative,setNarrative]=useState("");
  const [selectedLearnerId,setSelectedLearnerId]=useState("");
  const [selectedAchievementDefId,setSelectedAchievementDefId]=useState("");
  const [periodKey,setPeriodKey]=useState("2026");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_evaluation_achievement_workbench",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workbench;
      setData(next);

      setSelectedEvidenceId((current)=>
        next.verifiedEvidence.some((item)=>item.evidenceId===current)
          ? current
          : next.verifiedEvidence[0]?.evidenceId || ""
      );

      setSelectedEvalDefId((current)=>
        next.evaluationDefinitions.some((item)=>item.definitionId===current)
          ? current
          : next.evaluationDefinitions[0]?.definitionId || ""
      );

      setSelectedEvalId((current)=>
        next.evaluations.some((item)=>item.evaluationResultId===current)
          ? current
          : next.evaluations.find((item)=>item.status==="DRAFT")?.evaluationResultId
            || next.evaluations[0]?.evaluationResultId
            || ""
      );

      setSelectedLearnerId((current)=>
        next.learners.some((item)=>item.learnerId===current)
          ? current
          : next.learners[0]?.learnerId || ""
      );

      setSelectedAchievementDefId((current)=>
        next.achievementDefinitions.some((item)=>item.definitionId===current)
          ? current
          : next.achievementDefinitions[0]?.definitionId || ""
      );

      setSelectedAchievementId((current)=>
        next.achievements.some((item)=>item.achievementId===current)
          ? current
          : next.achievements.find((item)=>item.status==="CANDIDATE")?.achievementId
            || next.achievements[0]?.achievementId
            || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const selectedEval=useMemo(
    ()=>data?.evaluations.find((item)=>item.evaluationResultId===selectedEvalId) || null,
    [data,selectedEvalId]
  );

  const selectedEvidence=useMemo(
    ()=>data?.verifiedEvidence.find((item)=>item.evidenceId===selectedEvidenceId) || null,
    [data,selectedEvidenceId]
  );

  const selectedEvalDef=useMemo(
    ()=>data?.evaluationDefinitions.find((item)=>item.definitionId===selectedEvalDefId) || null,
    [data,selectedEvalDefId]
  );

  const selectedAchievement=useMemo(
    ()=>data?.achievements.find((item)=>item.achievementId===selectedAchievementId) || null,
    [data,selectedAchievementId]
  );

  const selectedAchievementDef=useMemo(
    ()=>data?.achievementDefinitions.find((item)=>item.definitionId===selectedAchievementDefId) || null,
    [data,selectedAchievementDefId]
  );

  const draftEvalCount=data?.evaluations.filter((item)=>item.status==="DRAFT").length || 0;
  const candidateAchievementCount=data?.achievements.filter((item)=>item.status==="CANDIDATE").length || 0;

  async function run(
    key:string,
    action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>
  ){
    setBusy(key);
    setError("");
    try{
      const result=await action();
      if(result.error) throw new Error(result.error.message);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{
      setBusy("");
    }
  }

  async function requestCredential(
    achievement:AchievementRow,
    credentialDefinitionId:string
  ){
    const key="center-"+
      achievement.achievementId.replaceAll("-","")+
      "-"+
      credentialDefinitionId.replaceAll("-","");

    await run("credential-request",()=>client.rpc("center_request_credential",{
      p_school_id:schoolId,
      p_achievement_id:achievement.achievementId,
      p_credential_definition_id:credentialDefinitionId,
      p_idempotency_key:key
    }));
  }

  if(!data) return <p className="muted">평가·성취 Workbench를 불러오는 중…</p>;

  return (
    <section className="evaluation-workbench-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Evaluation / Achievement Workbench</p>
          <h2>평가·성취</h2>
          <p className="route-description">
            검증된 Evidence에서 평가를 만들고, 확정된 평가를 성취·자격증명으로 이어갑니다.
          </p>
        </div>
        <span className="phase-badge">BUILD-10</span>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="workbench-count-grid">
        <Count label="검증 Evidence" value={data.verifiedEvidence.length} />
        <Count label="평가 확정 대기" value={draftEvalCount} emphasis />
        <Count label="확정 평가" value={data.evaluations.length-draftEvalCount} good />
        <Count label="성취 후보" value={candidateAchievementCount} emphasis />
      </div>

      <div className="workbench-filter-row">
        <button
          className={tab==="EVALUATION" ? "filter-chip active" : "filter-chip"}
          onClick={()=>setTab("EVALUATION")}
        >
          평가
        </button>
        <button
          className={tab==="ACHIEVEMENT" ? "filter-chip active" : "filter-chip"}
          onClick={()=>setTab("ACHIEVEMENT")}
        >
          성취·발급요청
        </button>
      </div>

      {tab==="EVALUATION" ? (
        <div className="evaluation-workbench-layout">
          <aside className="workbench-list-panel">
            <div className="panel-title">
              <strong>평가 결과</strong>
              <span>{data.evaluations.length}건</span>
            </div>
            {data.evaluations.map((item)=>(
              <button
                key={item.evaluationResultId}
                className={selectedEvalId===item.evaluationResultId ? "workbench-list-row active" : "workbench-list-row"}
                onClick={()=>setSelectedEvalId(item.evaluationResultId)}
              >
                <strong>{item.definitionName}</strong>
                <span>{item.displayName} · 점수 {item.normalizedScore ?? item.numericScore ?? "-"}</span>
                <small>{evalStatus(item.status)} · {formatDate(item.observedAt)}</small>
              </button>
            ))}
            {!data.evaluations.length && <p className="case-empty">평가 결과가 없습니다.</p>}
          </aside>

          <div className="workbench-detail-panel">
            <section className="workbench-section evaluation-create-card">
              <div className="panel-title">
                <strong>Evidence에서 평가 생성</strong>
                <span>검증된 근거만 사용</span>
              </div>
              <div className="workbench-form-grid">
                <label>
                  Evidence
                  <select value={selectedEvidenceId} onChange={(event)=>setSelectedEvidenceId(event.target.value)}>
                    {data.verifiedEvidence.map((item)=>(
                      <option key={item.evidenceId} value={item.evidenceId}>
                        {item.displayName} · {item.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  평가 기준
                  <select value={selectedEvalDefId} onChange={(event)=>setSelectedEvalDefId(event.target.value)}>
                    {data.evaluationDefinitions.map((item)=>(
                      <option key={item.definitionId} value={item.definitionId}>
                        {item.name} · {item.scoreMin}~{item.scoreMax}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  점수
                  <input
                    type="number"
                    min={selectedEvalDef?.scoreMin}
                    max={selectedEvalDef?.scoreMax}
                    value={score}
                    onChange={(event)=>setScore(event.target.value)}
                  />
                </label>
                <label className="wide">
                  평가 메모
                  <input
                    value={narrative}
                    onChange={(event)=>setNarrative(event.target.value)}
                    placeholder="평가 판단 근거"
                  />
                </label>
              </div>
              {selectedEvidence && (
                <p className="studio-hint">
                  {selectedEvidence.displayName} · {selectedEvidence.evidenceType} · {selectedEvidence.title}
                </p>
              )}
              <button
                className="primary compact-button"
                disabled={busy!=="" || !selectedEvidenceId || !selectedEvalDefId || score===""}
                onClick={async()=>{
                  const id=await run("evaluation-create",()=>client.rpc("center_create_evaluation_from_evidence",{
                    p_school_id:schoolId,
                    p_evidence_id:selectedEvidenceId,
                    p_definition_id:selectedEvalDefId,
                    p_numeric_score:Number(score),
                    p_narrative:narrative || null
                  }));
                  if(id) setSelectedEvalId(String(id));
                }}
              >
                평가 초안 생성
              </button>
            </section>

            {!selectedEval ? <p className="case-empty">평가를 생성하거나 선택하세요.</p> : (
              <>
                <header className="workbench-detail-head">
                  <div>
                    <p className="eyebrow">{selectedEval.definitionName}</p>
                    <h2>{selectedEval.displayName}</h2>
                    <p>
                      원점수 {selectedEval.numericScore ?? "-"}
                      {" · "}
                      정규화 {selectedEval.normalizedScore ?? "확정 전"}
                    </p>
                  </div>
                  <span className={selectedEval.status==="CONFIRMED" ? "status-pill good" : "status-pill"}>
                    {evalStatus(selectedEval.status)}
                  </span>
                </header>

                <section className="workbench-section">
                  <h3>연결 Evidence</h3>
                  {selectedEval.evidence.map((item)=>(
                    <div className="review-line" key={item.evidenceId}>
                      <div>
                        <strong>{item.title}</strong>
                        <span>{item.type}</span>
                      </div>
                      <span className={item.status==="VERIFIED" ? "status-pill good" : "status-pill"}>
                        {item.status==="VERIFIED" ? "검증 완료" : item.status}
                      </span>
                    </div>
                  ))}
                  {!selectedEval.evidence.length && <p className="case-empty">연결 Evidence가 없습니다.</p>}
                </section>

                {selectedEval.status==="DRAFT" && (
                  <section className="workbench-action-card">
                    <p>
                      연결된 Evidence가 모두 검증된 경우 평가를 확정합니다.
                      확정 시 연결된 Competency Mapping이 있으면 CompetencyResult가 생성됩니다.
                    </p>
                    <button
                      className="primary"
                      disabled={
                        busy!=="" ||
                        selectedEval.evidence.length===0 ||
                        selectedEval.evidence.some((item)=>item.status!=="VERIFIED")
                      }
                      onClick={()=>run("evaluation-confirm",()=>client.rpc("center_confirm_evaluation",{
                        p_school_id:schoolId,
                        p_evaluation_result_id:selectedEval.evaluationResultId,
                        p_expected_version:selectedEval.version,
                        p_reason:"평가·성취 Workbench에서 평가 확정"
                      }))}
                    >
                      평가 확정
                    </button>
                  </section>
                )}

                {selectedEval.status==="CONFIRMED" && (
                  <section className="workbench-section">
                    <h3>생성된 역량 결과</h3>
                    {selectedEval.competencies.map((item)=>(
                      <div className="review-line" key={item.competencyResultId}>
                        <div>
                          <strong>{item.competencyName}</strong>
                          <span>가중치 {item.weight}</span>
                        </div>
                        <span className="status-pill good">{item.score}</span>
                      </div>
                    ))}
                    {!selectedEval.competencies.length && (
                      <p className="case-empty">
                        이 평가 정의에는 Competency Mapping이 없습니다.
                      </p>
                    )}
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="achievement-workbench-layout">
          <aside className="workbench-list-panel">
            <div className="panel-title">
              <strong>성취</strong>
              <span>{data.achievements.length}건</span>
            </div>
            {data.achievements.map((item)=>(
              <button
                key={item.achievementId}
                className={selectedAchievementId===item.achievementId ? "workbench-list-row active" : "workbench-list-row"}
                onClick={()=>setSelectedAchievementId(item.achievementId)}
              >
                <strong>{item.definitionName}</strong>
                <span>{item.displayName} · {item.periodKey}</span>
                <small>{achievementStatus(item.status)}</small>
              </button>
            ))}
            {!data.achievements.length && <p className="case-empty">성취 후보가 없습니다.</p>}
          </aside>

          <div className="workbench-detail-panel">
            <section className="workbench-section achievement-create-card">
              <div className="panel-title">
                <strong>성취 후보 생성</strong>
                <span>확정 평가·검증 Evidence 자동 연결</span>
              </div>
              <div className="workbench-form-grid">
                <label>
                  학생
                  <select value={selectedLearnerId} onChange={(event)=>setSelectedLearnerId(event.target.value)}>
                    {data.learners.map((item)=>(
                      <option key={item.learnerId} value={item.learnerId}>
                        {item.displayName} · {item.gradeLevel || "-"}학년 {item.classLabel || ""}반
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  성취 기준
                  <select value={selectedAchievementDefId} onChange={(event)=>setSelectedAchievementDefId(event.target.value)}>
                    {data.achievementDefinitions.map((item)=>(
                      <option key={item.definitionId} value={item.definitionId}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  기간 키
                  <input value={periodKey} onChange={(event)=>setPeriodKey(event.target.value)} />
                </label>
              </div>

              {selectedAchievementDef && (
                <div className="criteria-preview">
                  {selectedAchievementDef.criteria.map((criterion)=>(
                    <span key={criterion.criterionId}>
                      {criterionLabel(criterion.criterionType)}
                      {criterion.minNormalizedScore!=null ? " ≥ "+criterion.minNormalizedScore : ""}
                      {criterion.minVerifiedEvidenceCount!=null ? " ≥ "+criterion.minVerifiedEvidenceCount+"건" : ""}
                    </span>
                  ))}
                </div>
              )}

              <button
                className="primary compact-button"
                disabled={busy!=="" || !selectedLearnerId || !selectedAchievementDefId || !periodKey.trim()}
                onClick={async()=>{
                  const id=await run("achievement-create",()=>client.rpc("center_create_achievement_candidate",{
                    p_school_id:schoolId,
                    p_learner_id:selectedLearnerId,
                    p_definition_id:selectedAchievementDefId,
                    p_period_key:periodKey.trim()
                  }));
                  if(id) setSelectedAchievementId(String(id));
                }}
              >
                성취 후보 생성
              </button>
            </section>

            {!selectedAchievement ? <p className="case-empty">성취 후보를 생성하거나 선택하세요.</p> : (
              <>
                <header className="workbench-detail-head">
                  <div>
                    <p className="eyebrow">{selectedAchievement.periodKey}</p>
                    <h2>{selectedAchievement.definitionName}</h2>
                    <p>
                      {selectedAchievement.displayName}
                      {" · "}
                      평가 {selectedAchievement.linkedEvaluationCount}건
                      {" · "}
                      증빙 {selectedAchievement.linkedEvidenceCount}건
                    </p>
                  </div>
                  <span className={selectedAchievement.status==="CONFIRMED" ? "status-pill good" : "status-pill"}>
                    {achievementStatus(selectedAchievement.status)}
                  </span>
                </header>

                {selectedAchievement.status==="CANDIDATE" && (
                  <section className="workbench-action-card">
                    <p>
                      필수 성취기준을 서버에서 다시 계산한 뒤 모두 충족할 때만 확정됩니다.
                    </p>
                    <button
                      className="primary"
                      disabled={busy!==""}
                      onClick={()=>run("achievement-confirm",()=>client.rpc("center_confirm_achievement",{
                        p_school_id:schoolId,
                        p_achievement_id:selectedAchievement.achievementId,
                        p_expected_version:selectedAchievement.version,
                        p_reason:"평가·성취 Workbench에서 성취 확정"
                      }))}
                    >
                      성취 확인
                    </button>
                  </section>
                )}

                {selectedAchievement.status==="CONFIRMED" && (
                  <section className="workbench-section">
                    <h3>디지털 자격증명 요청</h3>
                    {selectedAchievement.credentialDefinitions.map((credential)=>(
                      <div className="credential-request-row" key={credential.credentialDefinitionId}>
                        <div>
                          <strong>{credential.name}</strong>
                          <span>
                            {credential.validityDays ? "유효기간 "+credential.validityDays+"일" : "만료기간 없음"}
                          </span>
                        </div>
                        {credential.existingRequest ? (
                          <span className="status-pill">
                            {credential.existingRequest.status}
                          </span>
                        ) : (
                          <button
                            className="primary compact-button"
                            disabled={busy!==""}
                            onClick={()=>requestCredential(
                              selectedAchievement,
                              credential.credentialDefinitionId
                            )}
                          >
                            발급 요청
                          </button>
                        )}
                      </div>
                    ))}
                    {!selectedAchievement.credentialDefinitions.length && (
                      <p className="case-empty">
                        이 성취정의에 연결된 발행 가능한 자격증명이 없습니다.
                      </p>
                    )}
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Count({
  label,value,emphasis=false,good=false
}:{
  label:string;value:number;emphasis?:boolean;good?:boolean;
}){
  const className=good
    ? "workbench-count-card good"
    : emphasis
      ? "workbench-count-card emphasis"
      : "workbench-count-card";

  return <div className={className}><span>{label}</span><strong>{value}</strong></div>;
}
